import { apiClient, occmClient, setAccessToken } from "utils/clients";
import _ from "lodash";
import { handleAIDEToken } from "utils/darkSite";
import { logUserActions } from "utils/auth/common";
import { setUserId } from "utils/localstorage";

// Refresh this many ms before token expiry to keep the session alive.
// The user's session stays valid for any work that happens after a long-running operation (e.g. a multi-part upload) finishes, instead of being logged out.

// 35 seconds buffer before token expiry
const TOKEN_RENEW_BUFFER_MS = 35000;

let renewTokenTimer = null;

const clearRenewTokenTimer = () => {
    if (renewTokenTimer) {
        clearTimeout(renewTokenTimer);
        renewTokenTimer = null;
    }
};

const scheduleTokenRenewal = (dispatch, accessToken) => {
    clearRenewTokenTimer();
    try {
        const tokenData = handleAIDEToken(accessToken);
        const expSeconds = tokenData?.tokenData?.payload?.exp;
        if (!expSeconds) {
            return;
        }
        const renewInMs = (expSeconds * 1000) - Date.now() - TOKEN_RENEW_BUFFER_MS;
        if (renewInMs <= 0) {
            dispatch({ type: 'AUTH:AIDP-RENEW-TOKEN' });
            return;
        }
        renewTokenTimer = setTimeout(() => {
            dispatch({ type: 'AUTH:AIDP-RENEW-TOKEN' });
        }, renewInMs);
    } catch (err) {
        console.error('Failed to schedule AIDP token renewal:', err);
    }
};

const loginSuccess = (dispatch, payload, aidpLoginToken, userId) => {
    dispatch({
        type: 'AUTH:REFRESH-SSO-SUCCESS',
        payload: payload
    });
    setAccessToken(aidpLoginToken);
    setUserId({ userId });
    logUserActions("LOGIN_USER", userId);
    scheduleTokenRenewal(dispatch, aidpLoginToken);
};

function getCookieStr(cookieName) {
    const searchKey = cookieName + "=";
    const cookieItem = _.find(
        _.map(document.cookie.split(';'), _.trim),
        item => _.startsWith(item, searchKey)
    );

    return cookieItem ? cookieItem.substring(searchKey.length) : null;
}

export default ({ dispatch, getState }) => next => async (action) => {
    const { type } = action;

    if (type === 'AUTH:INITIALIZE') {
        dispatch({
            type: "APP:AIDP-LOGIN-PROGRESS"
        });
        const aidpLoginToken = sessionStorage.getItem('ot');
        if (aidpLoginToken) {
            const tokenData = handleAIDEToken(aidpLoginToken);
            const userId = encodeURIComponent(tokenData?.sub);
            const payload = {
                accessToken: aidpLoginToken,
                "idTokenPayload": { ...tokenData, sub: userId },
                "scope": "",
                "paramsForUI": {}
            }
            const date = new Date();
            if (Math.floor(date.getTime() / 1000) > tokenData.tokenData.payload?.exp) {
                dispatch({
                    type: 'AUTH:LOGOUT'
                });
            } else {
                const headers = {
                    'Authorization': `Bearer ${aidpLoginToken}`
                };
                let noncompliant = 'NO-LICENSE';
                try {
                    const response = await occmClient.get(process.env.REACT_APP_AIDE_LICENSE_API_URL + '?fields=*&name=ai_data_engine', { headers });
                    const license = _.head(_.get(response, 'data.records'));
                    if (_.get(license, 'state', '') === 'compliant' || _.get(license, 'licenses[0].serial_number')) {
                        noncompliant = null;
                        // TODO Uncomment for march release 
                        // if (license && _.get(license, 'state', '') === 'noncompliant') {
                        //     const disableSize = _.get(license, 'licenses[0].capacity.disabled_size', 0);
                        //     const maxSize = _.get(license, 'licenses[0].capacity.maximum_size', 0);
                        //     if(disableSize >= maxSize) {
                        //         noncompliant = 'EXPIRED-LICENSE';
                        //     }
                        // }
                    }
                } catch (error) {
                    console.error('Error fetching licenses:', error);
                }
                loginSuccess(dispatch, noncompliant ? { ...payload, status: noncompliant } : { ...payload, status: "SUCCESS" }, aidpLoginToken, userId);
            }
        } else {
            dispatch({
                type: 'AUTH:LOGOUT'
            });
        }
    } else if (type === 'AUTH:AIDP-RENEW-TOKEN') {
        const refreshToken = sessionStorage.getItem('rt');
        if (!refreshToken) {
            dispatch({ type: 'AUTH:LOGOUT' });
            return next(action);
        }
        try {
            const response = await occmClient.post(
                process.env.REACT_APP_AIDE_TOKEN_API_URL,
                {
                    grant_type: 'refresh_token',
                    refresh_token: refreshToken
                }
            );
            const newAccessToken = _.get(response, 'data.access_token');
            const newRefreshToken = _.get(response, 'data.refresh_token');
            if (!newAccessToken || !newRefreshToken) {
                throw new Error('Token refresh response missing access_token or refresh_token');
            }
            sessionStorage.setItem('ot', newAccessToken);
            sessionStorage.setItem('rt', newRefreshToken);
            setAccessToken(newAccessToken);
            scheduleTokenRenewal(dispatch, newAccessToken);
        } catch (error) {
            console.error('AIDP token renewal failed:', error);
            dispatch({ type: 'AUTH:LOGOUT' });
        }
    } else if (type === "AUTH:LOGOUT") {
        clearRenewTokenTimer();
        sessionStorage.removeItem('ot');
        sessionStorage.removeItem('rt');
        const userId = _.get(getState(), "auth.userId");
        logUserActions("LOGOUT_USER", userId);
        // Call /security/logout API to invalidate the session
        try {
            const headers = {
                'DOT-CSRF-Token': getCookieStr('DOT-CSRF')
            };
            await apiClient.post(process.env.REACT_APP_AIDE_LOGOUT_API_URL, { headers });
        } catch (error) {
            console.log('Could not invalidate session:', error);
        }
        window.location.replace('/oidc/callback?logout=');
    }

    return next(action);
}